package com.tovtalk.ui.scaffold

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import com.tovtalk.ui.adaptive.LocalWindowSizeClass
import com.tovtalk.ui.adaptive.WindowSizeClass
import com.tovtalk.ui.theme.TovTalkTheme

/**
 * Per-screen scaffold with slot-based chrome.
 *
 * Sits **inside** the main-tabs [com.tovtalk.ui.components.AdaptiveScaffold] (which provides
 * the bottom bar / navigation rail at the container level). This scaffold is for the
 * individual screen's chrome — top bar, snackbar, optional content-width constraint.
 *
 * **Slots:**
 * - [topBar] — optional. Typical use: `topBar = { TovTalkTopBar(title = ..., onBack = ...) }`
 * - [snackbarHost] — optional. Default renders [LocalSnackbarHost.current]; override to
 *   reposition (e.g. top-anchored like `HomeScreen`).
 * - [content] — receives [PaddingValues] so screens can offset `LazyColumn` content correctly.
 *
 * **Deliberately NOT exposed:** drawer, FAB, per-screen bottom bar. Audit showed zero
 * usage across all 36 screens. Add slots when the first real use case appears.
 *
 * **Responsive behavior:** when [constrainContentWidth] is true and the current
 * [WindowSizeClass] is [WindowSizeClass.Expanded], the content lambda is wrapped in a
 * horizontally-centered [Box] with `maxWidth = ScaffoldDefaults.maxContentWidthExpanded`.
 * On Compact/Medium, no constraint is applied.
 *
 * @param modifier Modifier applied to the root [Scaffold].
 * @param topBar Top bar slot. Empty by default.
 * @param snackbarHost Snackbar host slot. Defaults to rendering [LocalSnackbarHost.current].
 * @param contentWindowInsets Window insets forwarded to [Scaffold.contentWindowInsets].
 *   Defaults to zero (see [ScaffoldDefaults.contentWindowInsets]).
 * @param constrainContentWidth When true, limits content to
 *   [ScaffoldDefaults.maxContentWidthExpanded] on [WindowSizeClass.Expanded] windows.
 * @param containerColor Background color. Defaults to `TovTalkTheme.colors.backgroundPrimary`.
 * @param content Main content slot. Receives [PaddingValues] from Material3 [Scaffold].
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ScreenScaffold(
    modifier: Modifier = Modifier,
    topBar: @Composable () -> Unit = {},
    snackbarHost: @Composable () -> Unit = {
        SnackbarHost(hostState = LocalSnackbarHost.current)
    },
    contentWindowInsets: WindowInsets = ScaffoldDefaults.contentWindowInsets,
    constrainContentWidth: Boolean = true,
    containerColor: Color = TovTalkTheme.colors.backgroundPrimary,
    content: @Composable (PaddingValues) -> Unit,
) {
    val snackbarHostState = remember { SnackbarHostState() }
    val windowSizeClass = LocalWindowSizeClass.current
    val shouldConstrain = constrainContentWidth && windowSizeClass == WindowSizeClass.Expanded

    CompositionLocalProvider(LocalSnackbarHost provides snackbarHostState) {
        Scaffold(
            modifier = modifier.fillMaxSize(),
            topBar = topBar,
            snackbarHost = snackbarHost,
            containerColor = containerColor,
            contentWindowInsets = contentWindowInsets,
        ) { paddingValues ->
            if (shouldConstrain) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.TopCenter,
                ) {
                    Box(
                        modifier = Modifier
                            .widthIn(max = ScaffoldDefaults.maxContentWidthExpanded)
                            .fillMaxHeight(),
                    ) {
                        content(paddingValues)
                    }
                }
            } else {
                content(paddingValues)
            }
        }
    }
}
